// Müşteri Yönetimi tablosunun sütunları ve hücre biçimlendirme yardımcıları.
// Anahtarlar Customers dokümanlarındaki alan adlarıdır.

// `label`: Turkish fallback (Excel headers); `id`: customers.columns.<id> in the current language.
export const CUSTOMER_COLUMNS = {
  'Başlık': { id: 'title', label: 'Başlık', type: 'text', width: 260 },
  'Bilgi': { id: 'info', label: 'Bilgi', type: 'text', width: 230 },
  'Durum': {
    id: 'status',
    label: 'Durum',
    type: 'select',
    width: 120,
    options: [
      { value: 'aktif', labelKey: 'customers.status.active' },
      { value: 'inaktif', labelKey: 'customers.status.inactive' },
    ],
  },
  'Ana Anlaşma Tarihi': { id: 'agreementDate', label: 'Ana Anlaşma Tarihi', type: 'date', width: 170 },
  'Ana Anlaşma Numarası': { id: 'agreementNumber', label: 'Ana Anlaşma Numarası', type: 'text', width: 200 },
  'Döviz Tipi': {
    id: 'currency',
    label: 'Döviz Tipi',
    type: 'select',
    width: 120,
    options: ['TRY', 'USD', 'EUR', 'DKK'].map((code) => ({ value: code, label: code })),
  },
  'Müşteri Hizmet Num.': { id: 'serviceNumber', label: 'Müşteri Hizmet Numarası', type: 'text', width: 200 },
  'Teslim Tarihi': { id: 'deliveryDate', label: 'Teslim Tarihi', type: 'date', width: 150 },
  'Vergi Numarası': { id: 'taxNumber', label: 'Vergi Numarası', type: 'text', width: 150 },
  'Yurtiçi mi?': {
    id: 'domestic',
    label: 'Yurtiçi mi?',
    type: 'select',
    width: 130,
    options: [
      { value: 'Evet', labelKey: 'customers.yes' },
      { value: 'Hayır', labelKey: 'customers.no' },
    ],
  },
  'Ödeme Periyodu': {
    id: 'paymentPeriod',
    label: 'Ödeme Periyodu',
    type: 'select',
    width: 150,
    options: [
      { value: 'Aylık', labelKey: 'customers.options.monthly' },
      { value: '3 Aylık', labelKey: 'customers.options.quarterly' },
      { value: '6 Aylık', labelKey: 'customers.options.halfYearly' },
      { value: 'Yıllık', labelKey: 'customers.options.yearly' },
    ],
  },
};

export const COLUMN_KEYS = Object.keys(CUSTOMER_COLUMNS);

// İlk açılışta görünen sütunlar (geniş ekranı dolduracak kadar)
export const DEFAULT_VISIBLE_COLUMNS = {
  'Başlık': true,
  'Bilgi': true,
  'Durum': true,
  'Ana Anlaşma Tarihi': true,
  'Ana Anlaşma Numarası': true,
  'Döviz Tipi': true,
  'Müşteri Hizmet Num.': false,
  'Teslim Tarihi': false,
  'Vergi Numarası': true,
  'Yurtiçi mi?': true,
  'Ödeme Periyodu': true,
};

export const MIN_COLUMN_WIDTH = 96;

// Sıra numarası: her zaman görünür, sayfa değişse de kesintisiz ilerler
export const INDEX_COLUMN = '#';
export const INDEX_COLUMN_WIDTH = 60;

// Kimlik sütunları: tablonun başındaysalar yatay kaydırmada sabit kalırlar.
export const PINNED_COLUMNS = [INDEX_COLUMN, 'Başlık'];

// Arama kutusunun baktığı alanlar
export const SEARCH_FIELDS = ['Başlık', 'Bilgi', 'Vergi Numarası'];

// Hemen uygulanan (en az 3 karakter beklemeyen) filtreler
const INSTANT_FILTERS = new Set(COLUMN_KEYS.filter((key) => CUSTOMER_COLUMNS[key].type !== 'text'));

// Durum filtresinin varsayılanı: yalnızca aktif müşteriler
export const DEFAULT_FILTERS = { 'Durum': 'aktif' };

const EMPTY_MARKERS = ['nan', 'nat', 'n/a', 'null', 'undefined'];

export const isEmptyValue = (value) =>
  value === null ||
  value === undefined ||
  value === '' ||
  Number.isNaN(value) ||
  (typeof value === 'string' && EMPTY_MARKERS.includes(value.trim().toLowerCase()));

/** Boş değerler için '-', diğerleri metin olarak (tablo hücreleri CSS ile kısaltır). */
export const displayValue = (value) => (isEmptyValue(value) ? '-' : String(value));

/** Excel çıktısında kullanılan kısaltma (eski davranış korunur). */
export const truncateText = (text, maxLength = 15) => {
  if (isEmptyValue(text)) return '-';
  const textStr = String(text);
  return textStr.length > maxLength ? `${textStr.substring(0, maxLength)}...` : textStr;
};

/** Müşteri adı hem 'Başlık' hem de 'Müşteri Adı' alanında tutulabiliyor. */
export const customerName = (customer) => customer?.['Başlık'] || customer?.['Müşteri Adı'] || '';

export const isActiveCustomer = (customer) => String(customer?.['Durum'] || '').toLowerCase() === 'aktif';

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Tarih alanı (date input'tan gelen 'YYYY-MM-DD') gün.ay.yıl olarak; başka
 * biçimde kaydedilmiş eski değerler olduğu gibi gösterilir.
 */
export const formatIsoDate = (value, locale = 'tr-TR') => {
  if (isEmptyValue(value)) return '-';
  const match = typeof value === 'string' ? value.trim().match(ISO_DATE) : null;
  if (!match) return String(value);
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])).toLocaleDateString(locale);
};

/** Ödeme periyodu formda gün sayısı olarak giriliyor; eski kayıtlarda "Aylık" gibi metinler var. */
export const formatPaymentPeriod = (t, value) => {
  if (isEmptyValue(value)) return '-';
  const text = String(value).trim();
  if (/^\d+$/.test(text)) return t('customers.paymentDays', { count: Number(text) });
  return getFilterValueLabel(t, 'Ödeme Periyodu', text);
};

/** Sütun başlığı (geçerli dilde). */
export const columnLabel = (t, key) => (key === INDEX_COLUMN ? '#' : t(`customers.columns.${CUSTOMER_COLUMNS[key].id}`));

/** Bir seçeneğin geçerli dildeki adı. */
export const optionLabel = (t, option) => (option.labelKey ? t(option.labelKey) : option.label);

/** Seçimli sütunlarda bir değerin adı (çip, hücre); diğerlerinde değerin kendisi. */
export const getFilterValueLabel = (t, key, value) => {
  const option = CUSTOMER_COLUMNS[key]?.options?.find((item) => String(item.value) === String(value));
  return option ? optionLabel(t, option) : String(value);
};

const hasFilterValue = (value) => value !== undefined && value !== null && String(value) !== '';

/**
 * Bir sütun filtresinin listeye gerçekten uygulanıp uygulanmadığı: durum,
 * seçim ve tarih filtreleri her değerde, metin filtreleri en az 3 karakterde
 * devreye girer (useCustomerDirectory'deki filtreleme ile aynı kural).
 */
export const getFilterState = (key, value) => {
  if (!hasFilterValue(value)) return 'off';
  if (INSTANT_FILTERS.has(key)) return 'applied';
  return String(value).trim().length >= 3 ? 'applied' : 'pending';
};
